import { useEffect } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import {
  AccountManager,
  getCookie,
  NotificationManager,
  SessionStorageManager,
  SiteConfigManager,
  StorageManager,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes, staticRoutes } from '@atlas/routing';

import {
  CXFROM_STORAGE_KEY,
  OAUTH_AUTH_TOKEN_COOKIE,
  OAUTH_FLOW_TYPE_STORAGE_KEY,
  OAUTH_FLOW_TYPES,
  OAUTH_REGISTER_INFO_COOKIE,
  SIGNUP_VIA_GOOGLE_STORAGE_KEY,
} from 'common/constants';
import { useSignupPopup } from 'src/components/SignUp/useSignUpPopup';

const { payment } = clientProfileRoutes;
const { deposit } = paymentRoutes;
const { CLIENT_PROFILE } = COMPONENT_TYPES;

const resolveOauthRedirect = ({
  clientProfilePath,
  cxFromPath,
  flowType,
  history,
  homePath,
  isPopupSignup,
  isRegistrationFlow,
  leaveAfterFirstStep,
  openSignupPopup,
  pageAfterRegistration,
}) => {
  if (isRegistrationFlow) {
    history.push(staticRoutes.signup);

    return;
  }

  if (flowType === OAUTH_FLOW_TYPES.SIGNUP_MULTISTEP) {
    const redirectPath = leaveAfterFirstStep || isPopupSignup ? homePath : staticRoutes.signup;

    history.push(redirectPath);

    if (!leaveAfterFirstStep && isPopupSignup) openSignupPopup();

    return;
  }

  if (flowType === OAUTH_FLOW_TYPES.SIGNUP) {
    history.push(
      cxFromPath || pageAfterRegistration || `${clientProfilePath}/${payment}/${deposit}`,
    );
    return;
  }

  history.push(cxFromPath || homePath);
};

const useOauth = () => {
  const match = useRouteMatch('/oauth2/:result');
  const history = useHistory();
  const { openSignupPopup } = useSignupPopup();
  const leaveAfterFirstStep = SiteConfigManager.getConfig(
    'config.config.registration.close_multistep_after_first_step',
    false,
  );
  const isPopupSignup = SiteConfigManager.getConfig(
    'config.config.registration.showInPopup',
    false,
  );
  const homePath =
    SiteConfigManager.getExtraConfig('components.0.path') ||
    SiteConfigManager.getExtraConfig('components.0.defaultPath', '/');
  const defaultPageAfterRegistration = SiteConfigManager.getConfig(
    'config.config.links.defaultPageAfterRegistration',
  );
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);

  useEffect(() => {
    if (!match) return;

    const cxFromPath = StorageManager.getItem(CXFROM_STORAGE_KEY);
    const flowType = StorageManager.getItem(OAUTH_FLOW_TYPE_STORAGE_KEY);

    const pageAfterRegistration =
      defaultPageAfterRegistration?.indexOf('//') > -1
        ? defaultPageAfterRegistration.split('/').reduce((acc, next, index) => {
            if (index < 3) return acc;
            return `${acc}/${next}`;
          }, '')
        : defaultPageAfterRegistration;

    const authorizeToken = getCookie(OAUTH_AUTH_TOKEN_COOKIE);
    const registrationInfoToken = getCookie(OAUTH_REGISTER_INFO_COOKIE);

    if (match.params.result === 'success') {
      if (registrationInfoToken) {
        // NOTE: Add cleanup of token after request with data implemented
        AccountManager.setOAuthRegisterToken(registrationInfoToken);
      } else if (authorizeToken) {
        AccountManager.setToken(authorizeToken);
        AccountManager.resolveAuthentication().then();
      }
    } else {
      NotificationManager.notify({
        message: 'social_login_error',
        status: 'error',
        type: 'notification',
      });
    }

    resolveOauthRedirect({
      clientProfilePath,
      cxFromPath,
      flowType,
      history,
      homePath,
      isPopupSignup,
      isRegistrationFlow: !!registrationInfoToken,
      leaveAfterFirstStep,
      openSignupPopup,
      pageAfterRegistration,
    });

    if (flowType === OAUTH_FLOW_TYPES.SIGNUP || flowType === OAUTH_FLOW_TYPES.SIGNUP_MULTISTEP) {
      SessionStorageManager.setItem(SIGNUP_VIA_GOOGLE_STORAGE_KEY, true);
    }

    StorageManager.removeItem(CXFROM_STORAGE_KEY);
    StorageManager.removeItem(OAUTH_FLOW_TYPE_STORAGE_KEY);
  }, [
    clientProfilePath,
    defaultPageAfterRegistration,
    history,
    homePath,
    isPopupSignup,
    leaveAfterFirstStep,
    match,
    openSignupPopup,
  ]);
};

export default useOauth;
